import React, { useState, useEffect } from 'react';
import { FiShield, FiLock, FiTrash2, FiUser, FiKey, FiCheckSquare, FiEye, FiEyeOff } from 'react-icons/fi';
import apiClient from '../utils/apiClient';
import '../styles/Admin.css';

const AdminTeam = () => {
  const [admins, setAdmins] = useState([]);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'admin',
    permissions: []
  });
  const [passwordChange, setPasswordChange] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [resetPassword, setResetPassword] = useState({ targetEmail: '', newPassword: '', confirmPassword: '' });
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [showRecoveryEmail, setShowRecoveryEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  // Password visibility states
  const [showAddMemberPassword, setShowAddMemberPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const AVAILABLE_ROLES = [
    { id: 'admin', label: 'Admin (Full Access)' },
    { id: 'product_manager', label: 'Product Manager' },
    { id: 'inventory_clerk', label: 'Inventory Clerk' },
    { id: 'viewer', label: 'Viewer (Read Only)' }
  ];

  const AVAILABLE_PERMISSIONS = [
    { id: 'view_products', label: 'View Products' },
    { id: 'edit_products', label: 'Edit Products' },
    { id: 'publish_products', label: 'Publish Products' },
    { id: 'manage_inventory', label: 'Manage Inventory' },
    { id: 'view_orders', label: 'View Orders' },
    { id: 'manage_orders', label: 'Manage Orders' },
    { id: 'manage_team', label: 'Manage Team' }
  ];

  useEffect(() => {
    fetchAdmins();
    getCurrentAdmin();
  }, []);

  const getCurrentAdmin = () => {
    const email = sessionStorage.getItem('adminEmail');
    const token = sessionStorage.getItem('adminToken');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.hasOwnProperty('isSuperAdmin') && payload.email === 'admin@kalingastore.shop') {
        payload.isSuperAdmin = true;
      }
      setCurrentAdmin(payload);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await apiClient.get('/admin/list');
      setAdmins(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setLoading(false);
    }
  };

  const handlePermissionChange = (permId) => {
    setFormData(prev => {
      const newPerms = prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId];
      return { ...prev, permissions: newPerms };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      // Map role to isSuperAdmin for backward compatibility if needed, 
      // but send full role/permissions object for backend to store
      const payload = {
        ...formData,
        isSuperAdmin: formData.role === 'admin' // Legacy support
      };

      await apiClient.post('/admin/register', payload);
      setSuccess('New team member added successfully!');
      setFormData({ email: '', password: '', name: '', role: 'product_manager', permissions: [] });
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add team member');
    }
  };

  // ... (Keep existing password change/reset handlers) ...
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (passwordChange.newPassword !== passwordChange.confirmPassword) { setError('New passwords do not match'); return; }
    if (passwordChange.newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    try {
      await apiClient.post('/admin/change-password', { oldPassword: passwordChange.oldPassword, newPassword: passwordChange.newPassword });
      setSuccess('Your password changed successfully!');
      setPasswordChange({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setShowChangePassword(false);
    } catch (err) { setError(err.response?.data?.error || 'Failed to change password'); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (resetPassword.newPassword !== resetPassword.confirmPassword) { setError('Passwords do not match'); return; }
    if (resetPassword.newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    try {
      await apiClient.post('/admin/reset-password', { targetEmail: resetPassword.targetEmail, newPassword: resetPassword.newPassword });
      setSuccess(`Password reset successfully for ${resetPassword.targetEmail}`);
      setResetPassword({ targetEmail: '', newPassword: '', confirmPassword: '' });
      setShowResetPassword(false);
    } catch (err) { setError(err.response?.data?.error || 'Failed to reset password'); }
  };

  const handleDeleteAdmin = async (email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}?`)) return;
    setError('');
    setSuccess('');
    try {
      await apiClient.delete(`/admin/delete/${email}`);
      setSuccess('Team member deleted successfully!');
      fetchAdmins();
    } catch (err) { setError(err.response?.data?.error || 'Failed to delete team member'); }
  };

  const handleSetRecoveryEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await apiClient.post('/admin/set-recovery-email', { recoveryEmail });
      setSuccess('Recovery email set successfully!');
      setShowRecoveryEmail(false);
      setRecoveryEmail('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to set recovery email'); }
  };

  const isSuperAdmin = currentAdmin?.isSuperAdmin || false;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Manage Team & Permissions</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => setShowChangePassword(!showChangePassword)}
            className="admin-btn"
            style={{
              background: '#2196f3',
              width: 'auto',
              marginTop: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            <FiKey /> Change Password
          </button>
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: '1rem', padding: '1rem', background: '#ffebee', color: '#c62828', borderRadius: '4px' }}>{error}</div>}
      {success && <div className="success-message" style={{ marginBottom: '1rem', padding: '1rem', background: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>{success}</div>}
      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="admin-card" style={{ marginBottom: '2rem', border: '2px solid #2196f3' }}>
          <h3>Change Your Password</h3>
          <form onSubmit={handleChangePassword} className="admin-form">
            <div className="form-group">
              <label>Current Password</label>
              <div className="password-input-container">
                <input
                  type={showOldPassword ? "text" : "password"}
                  value={passwordChange.oldPassword}
                  onChange={(e) => setPasswordChange({ ...passwordChange, oldPassword: e.target.value })}
                  required
                />
                <span
                  className="password-toggle-icon"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                >
                  {showOldPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
            </div>
            <div className="form-group">
              <label>New Password</label>
              <div className="password-input-container">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordChange.newPassword}
                  onChange={(e) => setPasswordChange({ ...passwordChange, newPassword: e.target.value })}
                  required
                />
                <span
                  className="password-toggle-icon"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordChange.confirmPassword}
                  onChange={(e) => setPasswordChange({ ...passwordChange, confirmPassword: e.target.value })}
                  required
                />
                <span
                  className="password-toggle-icon"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" className="admin-btn">Change Password</button>
              <button type="button" onClick={() => setShowChangePassword(false)} className="admin-btn" style={{ background: '#999' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-content-grid">
        {/* Add New Member Form */}
        {isSuperAdmin && (
          <div className="admin-card">
            <h3><FiUser /> Add New Team Member</h3>
            <form onSubmit={handleSubmit} className="admin-form" style={{ padding: '0', boxShadow: 'none' }}>
              <div className="form-group">
                <label>Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <div className="password-input-container">
                  <input
                    type={showAddMemberPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength="8"
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={() => setShowAddMemberPassword(!showAddMemberPassword)}
                  >
                    {showAddMemberPassword ? <FiEyeOff /> : <FiEye />}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label>Role</label>
                <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                  {AVAILABLE_ROLES.map(role => (
                    <option key={role.id} value={role.id}>{role.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Permissions</label>
                <div className="permissions-grid">
                  {AVAILABLE_PERMISSIONS.map(perm => (
                    <div key={perm.id} className="permission-item">
                      <input
                        type="checkbox"
                        id={`perm-${perm.id}`}
                        checked={formData.permissions.includes(perm.id)}
                        onChange={() => handlePermissionChange(perm.id)}
                      />
                      <label htmlFor={`perm-${perm.id}`}>{perm.label}</label>
                    </div>
                  ))}
                </div>
              </div>

              <button type="submit" className="add-btn" style={{ width: '100%', justifyContent: 'center', marginTop: '1rem' }}>Add Member</button>
            </form>
          </div>
        )}

        {/* Team List */}
        <div className="admin-card">
          <h3>Team Members</h3>
          {loading ? <p>Loading...</p> : (
            <div className="table-responsive">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    {isSuperAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {admins.map(admin => (
                    <tr key={admin.id || admin._id}>
                      <td>
                        <div style={{ fontWeight: '500' }}>{admin.name}</div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>{admin.email}</div>
                      </td>
                      <td>
                        <span className="role-badge" style={{
                          background: admin.isSuperAdmin ? '#ffebee' : '#e3f2fd',
                          color: admin.isSuperAdmin ? '#c62828' : '#1565c0'
                        }}>
                          {admin.role ? AVAILABLE_ROLES.find(r => r.id === admin.role)?.label || admin.role : (admin.isSuperAdmin ? 'Admin' : 'Member')}
                        </span>
                      </td>
                      {isSuperAdmin && (
                        <td>
                          {!admin.isSuperAdmin && (
                            <button
                              onClick={() => handleDeleteAdmin(admin.email)}
                              style={{ background: 'none', border: 'none', color: '#f44336', cursor: 'pointer' }}
                              title="Delete Member"
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTeam;

